import { PROJECT_PERMISSIONS } from '@pm/shared';

/**
 * Klickdummy 1:1 — Projekt-Sekundär-Sidebar.
 * Phasen-Reihenfolge entspricht dem PM-Lifecycle: Übersicht (kontextfrei),
 * Initiierung → Planung → Ausführung → Abschluss. Items pro Phase folgen
 * der Mockup-Reihenfolge unter packages/api/projectmanagement/UI_MOCKUP.html.
 */

export type ProjectPhaseKey = 'overview' | 'initiation' | 'planning' | 'execution' | 'closure';

export interface ProjectSidebarItem {
  key: string;
  labelKey: string;
  permission: string;
  /** Optionaler Reifegrad-Schritt (1-17). Wenn gesetzt, zeigt die Sidebar
   *  eine Ampel-Markierung basierend auf dem dashboard.schritte[].reifegrad. */
  step?: number;
  /** Optionale Unterpunkte (eine Verschachtelungsebene). Der Eltern-Eintrag
   *  bleibt klickbar (eigene Seite) und ist zusaetzlich auf-/zuklappbar. */
  children?: ProjectSidebarItem[];
  /** Direkt-Label (statt labelKey-Uebersetzung) — fuer dynamisch erzeugte
   *  Eintraege wie Arbeitspaket-Subpunkte. */
  label?: string;
  /** Dynamischer Arbeitspaket-Subpunkt: navigiert zu projektplan?ap=<apId>
   *  statt zu /projects/:id/<key>. */
  apId?: string;
}

export interface ProjectSidebarGroup {
  key: ProjectPhaseKey;
  labelKey: string;
  items: ProjectSidebarItem[];
}

/** PHQ-111: Die Prozesslandkarte (PHQ-63 ausgeblendet) ist durch die
 *  „Programmübersicht“ ersetzt und wieder sichtbar. Route-Key bleibt `prozesslandkarte`. */
export const PROZESSLANDKARTE_ENABLED = true;

export const PROJECT_SIDEBAR_GROUPS: ProjectSidebarGroup[] = [
  {
    key: 'overview',
    labelKey: 'sidebar.groups.overview',
    items: [
      { key: 'dashboard', labelKey: 'sidebar.items.dashboard', permission: PROJECT_PERMISSIONS.PROJECT.READ },
      ...(PROZESSLANDKARTE_ENABLED
        ? [{ key: 'prozesslandkarte', labelKey: 'sidebar.items.prozesslandkarte', label: 'Programmübersicht', permission: PROJECT_PERMISSIONS.PROJECT.READ }]
        : []),
      { key: 'stakeholders', labelKey: 'sidebar.items.stakeholders', permission: PROJECT_PERMISSIONS.STAKEHOLDER.READ },
    ],
  },
  {
    key: 'initiation',
    labelKey: 'sidebar.groups.initiation',
    // Hauptpunkte: Machbarkeit + Projektauftrag. Ziele, Rahmenbedingungen, Team,
    // Unsicherheiten und Zeiten sind Unterpunkte des Projektauftrags. Die
    // Schritt-Nummern (Reifegrad-Datenbasis) bleiben unveraendert.
    items: [
      { key: 'machbarkeit', labelKey: 'sidebar.items.machbarkeit', permission: PROJECT_PERMISSIONS.PROJECT_INITIALIZATION.READ, step: 1 },
      {
        key: 'projektauftrag', labelKey: 'sidebar.items.projektauftrag', permission: PROJECT_PERMISSIONS.PROJECT_CONFIG.READ, step: 3,
        // Rahmenbedingungen (Felder -> Machbarkeit) und Unsicherheiten (Bekannte
        // Unbekannte -> Ziele) wurden 2026-06-03 aufgeloest. Schritt 2/7 bleiben
        // in der Reifegrad-Datenbasis, sind aber nicht mehr navigierbar.
        children: [
          { key: 'ziele', labelKey: 'sidebar.items.ziele', permission: PROJECT_PERMISSIONS.PROJECT_INITIALIZATION.READ, step: 4 },
          { key: 'team', labelKey: 'sidebar.items.team', permission: PROJECT_PERMISSIONS.TEAM.READ, step: 6 },
          { key: 'zeiten', labelKey: 'sidebar.items.zeiten', permission: PROJECT_PERMISSIONS.PROJECT_INITIALIZATION.READ, step: 5 },
        ],
      },
    ],
  },
  {
    key: 'planning',
    labelKey: 'sidebar.groups.planning',
    items: [
      // „Projektplan" bewusst ganz oben in der Planung (User-Wunsch); die step-
      // Nummern bleiben unverändert (Reifegrad-Datenbasis, positionsunabhängig).
      { key: 'projektplan', labelKey: 'sidebar.items.projektplan', permission: PROJECT_PERMISSIONS.TASK.READ, step: 11 },
      { key: 'anforderungen', labelKey: 'sidebar.items.anforderungen', permission: PROJECT_PERMISSIONS.ANFORDERUNG.READ, step: 8 },
      { key: 'scope', labelKey: 'sidebar.items.scope', permission: PROJECT_PERMISSIONS.PROJECT_CONFIG.READ, step: 9 },
      { key: 'arbeitspakete', labelKey: 'sidebar.items.arbeitspakete', permission: PROJECT_PERMISSIONS.ARBEITSPAKET.READ, step: 10 },
      { key: 'risiken', labelKey: 'sidebar.items.risiken', permission: PROJECT_PERMISSIONS.RISIKO.READ, step: 12 },
      { key: 'meetings', labelKey: 'sidebar.items.meetings', permission: PROJECT_PERMISSIONS.MEETING.READ },
      { key: 'offene-punkte', labelKey: 'sidebar.items.offenepunkte', permission: PROJECT_PERMISSIONS.OFFENER_PUNKT.READ, step: 13 },
    ],
  },
  {
    key: 'execution',
    labelKey: 'sidebar.groups.execution',
    items: [
      { key: 'kanban', labelKey: 'sidebar.items.kanban', permission: PROJECT_PERMISSIONS.TASK.READ, step: 14 },
      { key: 'burnup', labelKey: 'sidebar.items.burnup', permission: PROJECT_PERMISSIONS.TASK.READ, step: 15 },
      { key: 'status', labelKey: 'sidebar.items.status', permission: PROJECT_PERMISSIONS.WEEKLY_PROGRESS.READ, step: 16 },
      { key: 'retro', labelKey: 'sidebar.items.retro', permission: PROJECT_PERMISSIONS.RETRO.READ, step: 17 },
    ],
  },
  {
    key: 'closure',
    labelKey: 'sidebar.groups.closure',
    items: [
      // Abschluss bewusst ohne `step` — keine Reifegrad-Ampel, kein Phase-Gating-Beitrag.
      { key: 'abschluss', labelKey: 'sidebar.items.abschluss', permission: PROJECT_PERMISSIONS.PROJECT_ABSCHLUSS.READ },
    ],
  },
];

export const PROJECT_PHASE_ORDER: ProjectPhaseKey[] = ['initiation', 'planning', 'execution', 'closure'];

/** Flacht Items inkl. Unterpunkte (eine Ebene tief) in eine Liste. Wird ueberall
 *  gebraucht, wo Schritt-Nummern oder gueltige Routen-Keys gesammelt werden. */
export function flattenSidebarItems(items: ProjectSidebarItem[]): ProjectSidebarItem[] {
  const out: ProjectSidebarItem[] = [];
  for (const item of items) {
    out.push(item);
    if (item.children?.length) out.push(...flattenSidebarItems(item.children));
  }
  return out;
}

/** Findet die Gruppe zu einem Phase-Key (inkl. Unterpunkte). Default: overview. */
export function findGroupForPhase(phaseKey: string): ProjectSidebarGroup {
  for (const group of PROJECT_SIDEBAR_GROUPS) {
    if (flattenSidebarItems(group.items).some((i) => i.key === phaseKey)) return group;
  }
  return PROJECT_SIDEBAR_GROUPS[0]!;
}

/** Default-Phase wenn /projects/:id ohne Suffix aufgerufen wird. */
export const PROJECT_DEFAULT_PHASE = 'dashboard';

/**
 * Legacy ?tab=-Werte auf neue Phase-Routen mappen.
 */
export interface LegacyTabRedirect {
  phase?: string;
}

export function mapLegacyTab(legacyTab: string): LegacyTabRedirect {
  if (legacyTab === 'overview') return { phase: 'dashboard' };
  if (legacyTab === 'weekly-progress') return { phase: 'status' };
  if (legacyTab === 'requirements') return { phase: 'anforderungen' };
  // Übrige: existieren als Phase-Item
  return { phase: legacyTab };
}
